import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  User, Phone, Mail, MapPin, Globe, MessageSquare, Loader2, CheckCircle2,
  ArrowRight, ShieldCheck, Sparkles, AlertCircle, Briefcase, PhoneCall, FileCheck2,
  LayoutGrid, MousePointerClick,
} from 'lucide-react';
import {
  INDIAN_STATES, LANGUAGES, NAV_CATEGORIES, SERVICE_CATALOG, isIncomeTaxService,
  defaultSpokenLanguageForUiLocale,
} from '../mock';
import { ServiceMarquee } from '../components/ServiceMarquee';
import FormLegalNotice from '../components/FormLegalNotice';
import ServiceContent from '../components/ServiceContent';
import { SERVICE_CONTENT } from '../data/serviceContent';
import { useLanguage } from '../i18n/LanguageContext';
import {
  serviceTitle, serviceDesc,
} from '../i18n/serviceCopy';
import {
  buildCampaignCapturePayload,
  getJourneyId,
  trackEvent,
  trackPageLifecycle,
} from '../lib/customerEventTracker';
import { appendLeadToGoogleSheets, isGoogleSheetsConfigured } from '../lib/googleSheetsLead';
import { scrollToAnchor } from '../lib/scrollToAnchor';
import { goToPostSubmit } from '../lib/postSubmitGuest';

const initialState = {
  full_name: '', email: '', mobile: '',
  state: '', language: '', remark: '', service_required: [],
};

const GST_REGISTRATION_ENTITY_GUIDE = [
  {
    entityType: 'PROPRIETARY',
    label: 'Proprietor GST Registration',
    docs: [
      'PAN Card of proprietor',
      'Aadhaar card of proprietor',
      'Passport size photograph',
      'Property tax receipt for registered office',
      'Rental agreement (if premises is rented)',
      'NOC (if premises is owned by another person)',
      'Cancelled cheque / passbook / bank statement',
    ],
  },
  {
    entityType: 'PARTNERSHIP_FIRM',
    label: 'Partnership Firm GST Registration',
    docs: [
      'PAN card of all partners',
      'Aadhaar card of all partners',
      'Passport photos of partners',
      'Property tax receipt for office address',
      'Rental agreement (if office is rented)',
      'NOC (if premises owned by someone else)',
      'Cancelled cheque / passbook / bank statement',
      'Partnership deed and registration certificate',
      'Authorization letter for GST filing',
    ],
  },
  {
    entityType: 'COMPANY',
    label: 'Company GST Registration',
    docs: [
      'PAN card of all directors',
      'Aadhaar card of all directors',
      'Passport photos of directors',
      'Property tax receipt for office address',
      'Rental agreement (if office is rented)',
      'NOC (if premises owned by someone else)',
      'Cancelled cheque / passbook / bank statement',
      'Authorization letter for GST filing',
    ],
  },
];

const validateForm = (form, t) => {
  const e = {};
  if (!form.full_name.trim() || form.full_name.length > 150) e.full_name = t('form.err_name', 'Enter a valid name (up to 150 chars)');
  if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = t('form.err_email', 'Enter valid email');
  if (!/^[0-9]{10}$/.test(form.mobile)) e.mobile = t('form.err_mobile', 'Enter a valid 10-digit mobile number');
  if (form.service_required.length === 0) e.service_required = t('sf.err_services', 'Select at least one service');
  return e;
};

const FormSection = ({ title, icon: Icon, children, hint }) => (
  <div>
    <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
      <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center"><Icon className="w-4 h-4 text-[#14A098]" /></div>
      <h2 className="font-bold text-[#0f2540] text-lg">{title}</h2>
      {hint && <span className="text-xs text-gray-500 ml-auto">{hint}</span>}
    </div>
    {children}
  </div>
);

const FieldWrap = ({ label, icon: Icon, required, error, hint, htmlFor, children }) => (
  <div className="space-y-2">
    <Label htmlFor={htmlFor} className="text-sm font-medium text-[#0f2540] flex items-center gap-1.5">
      {Icon && <Icon className="w-3.5 h-3.5 text-[#14A098]" />} {label}
      {required && <span className="text-rose-500">*</span>}
    </Label>
    {children}
    {hint && !error && <p className="text-xs text-gray-400">{hint}</p>}
    {error && <p className="text-xs text-rose-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{error}</p>}
  </div>
);

const ServicePicker = ({ selected, onToggle, error, includeIncomeTax = false }) => {
  const { t } = useLanguage();
  const [query, setQuery] = useState('');
  // Default excludes income-tax services; embedded home form can include all.
  // Consultation has its own dedicated booking flow, so it never belongs in this multi-service picker.
  // NAV_CATEGORIES is a module-level constant — safe to omit from deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cats = useMemo(
    () => NAV_CATEGORIES
      .filter((c) => c.category !== 'CONSULTATION')
      .filter((c) => includeIncomeTax || c.category !== 'INCOME_TAX'),
    [includeIncomeTax],
  );
  const selectedServices = useMemo(
    () => SERVICE_CATALOG.filter((svc) => selected.includes(svc.code)),
    [selected],
  );
  const filteredCats = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return cats;
    return cats
      .map((cat) => ({
        ...cat,
        services: cat.services.filter((svc) => {
          const titleTr = serviceTitle(svc.code, t, svc.title);
          const descTr = serviceDesc(svc.code, t, svc.desc);
          return (
            titleTr.toLowerCase().includes(q)
            || descTr.toLowerCase().includes(q)
            || svc.title.toLowerCase().includes(q)
            || svc.desc.toLowerCase().includes(q)
            || svc.code.toLowerCase().includes(q)
          );
        }),
      }))
      .filter((cat) => cat.services.length > 0);
  }, [cats, query, t]);
  const [activeCategory, setActiveCategory] = useState('');

  useEffect(() => {
    if (!filteredCats.some((cat) => cat.category === activeCategory)) {
      setActiveCategory(filteredCats[0]?.category || '');
    }
  }, [filteredCats, activeCategory]);

  const activeServices = useMemo(
    () => filteredCats.find((cat) => cat.category === activeCategory)?.services || [],
    [filteredCats, activeCategory],
  );

  return (
    <div>
      <Label className="text-sm font-medium text-[#0f2540] mb-3 block">
        {t('sf.services_need', 'Services You Need')} <span className="text-rose-500">*</span>
      </Label>

      <Input
        id="services-search-input"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('sf.search_ph', 'Search services by name')}
        className="mb-3"
      />

      <div className="space-y-4 pr-2 border border-gray-100 rounded-xl p-4 bg-gray-50/50">
        <div className="flex flex-wrap gap-2">
          {filteredCats.map((cat) => (
            <button
              key={cat.category}
              type="button"
              onClick={() => setActiveCategory(cat.category)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                activeCategory === cat.category
                  ? 'border-[#14A098] bg-[#14A098] text-white'
                  : 'border-gray-200 bg-white text-[#0f2540] hover:border-[#14A098]/40'
              }`}
            >
              {t(`category.${cat.category}`, cat.label)} ({cat.services.length})
            </button>
          ))}
        </div>

        {activeCategory && (
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#14A098] mb-2">
              {(() => {
                const ac = filteredCats.find((cat) => cat.category === activeCategory);
                return ac ? t(`category.${ac.category}`, ac.label) : '';
              })()}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {activeServices.map((svc) => {
                const checked = selected.includes(svc.code);
                return (
                  <label
                    key={svc.code}
                    className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                      checked ? 'border-[#14A098] bg-teal-50' : 'border-gray-200 bg-white hover:border-[#14A098]/40'
                    }`}
                  >
                    <Checkbox checked={checked} onCheckedChange={() => onToggle(svc.code)} className="mt-0.5 data-[state=checked]:bg-[#14A098] data-[state=checked]:border-[#14A098]" />
                    <div className="flex-1 min-w-0">
                      <div className="text-base font-semibold text-[#0f2540] flex items-start gap-1.5 leading-snug">
                        <svc.icon className="w-4 h-4 text-[#14A098] mt-0.5 shrink-0" />
                        <span className="line-clamp-2">{serviceTitle(svc.code, t, svc.title)}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1 line-clamp-2">{serviceDesc(svc.code, t, svc.desc)}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {filteredCats.length === 0 && <p className="text-sm text-gray-500">{t('sf.no_match', 'No services match your search.')}</p>}
      </div>
      {error && <p className="text-xs text-rose-500 flex items-center gap-1 mt-2"><AlertCircle className="w-3 h-3" /> {error}</p>}
    </div>
  );
};

const Sidebar = ({ count, errors, submitError }) => {
  const { t } = useLanguage();
  const errorList = [
    ...(submitError ? [submitError] : []),
    ...Object.values(errors || {}).filter((value) => typeof value === 'string' && value.trim()),
  ];
  const uniqueErrors = [...new Set(errorList)];

  return (
  <div className="space-y-6">
    {uniqueErrors.length > 0 && (
      <Card className="p-5 rounded-2xl border border-rose-200 bg-rose-50">
        <h3 className="font-bold text-rose-700 mb-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {t('sf.fix_errors_title', 'Please fix these errors')}
        </h3>
        <ul className="space-y-2">
          {uniqueErrors.map((err) => (
            <li key={err} className="text-sm text-rose-700 flex items-start gap-2">
              <span className="mt-1 h-1.5 w-1.5 rounded-full bg-rose-500 shrink-0" />
              <span>{t('sf.fix_how', 'How to fix:')} {err}</span>
            </li>
          ))}
        </ul>
        <p className="text-xs text-rose-600 mt-3">
          {t('sf.need_help', 'Need help?')}{' '}
          <Link to="/contact" className="font-semibold underline hover:text-rose-700">
            {t('form.contact_support', 'Contact support')}
          </Link>
        </p>
      </Card>
    )}
    <Card className="p-6 rounded-2xl border border-gray-100 bg-white">
      <ol className="space-y-4">
        {[
          { id: 'pick', icon: Briefcase, title: t('sf.step_pick_title', 'Pick your services'), desc: t('sf.step_pick_desc', 'Select one or more services you need.') },
          { id: 'callback', icon: Phone, title: t('sf.step_cb_title', 'Get a callback'), desc: t('sf.step_cb_desc', 'A specialist contacts you within 2 hours.') },
          { id: 'deliver', icon: CheckCircle2, title: t('sf.step_deliver_title', 'We deliver'), desc: t('sf.step_deliver_desc', 'On-time delivery with full transparency.') },
        ].map((s, index) => (
          <li key={s.id} className="flex gap-3">
            <div className="w-9 h-9 rounded-full bg-teal-50 flex items-center justify-center shrink-0"><s.icon className="w-4 h-4 text-[#14A098]" /></div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#14A098]">{t('common.step_label', 'Step {n}', { n: index + 1 })}</div>
              <div className="text-sm font-semibold text-[#0f2540]">{s.title}</div>
              <div className="text-xs text-gray-500">{s.desc}</div>
            </div>
          </li>
        ))}
      </ol>
    </Card>
    <Card className="p-6 rounded-2xl bg-gradient-to-br from-[#0f2540] to-[#14A098] text-white">
      <div className="flex items-center gap-2 mb-3"><ShieldCheck className="w-5 h-5" /><span className="font-semibold">{t('sf.secure_title', 'Secure & Confidential')}</span></div>
      <p className="text-sm text-teal-100 leading-relaxed">{t('sf.secure_body', 'All data is transmitted over HTTPS and accessed only by authorised CAs. We never share your business details.')}</p>
    </Card>
    {count > 0 && (
      <Card className="p-6 rounded-2xl border border-[#14A098]/30 bg-teal-50/40">
        <div className="text-xs uppercase tracking-wider text-[#14A098] font-semibold mb-1">{t('sf.applying_for', 'You\'re applying for')}</div>
        <div className="text-2xl font-extrabold text-[#0f2540]">{count > 1 ? t('sf.service_count_plural', '{n} services', { n: count }) : t('sf.service_count', '{n} service', { n: count })}</div>
        <div className="text-xs text-gray-500 mt-1">{t('sf.bundle_note', 'Bundle discounts may apply.')}</div>
      </Card>
    )}
  </div>
  );
};

const GSTRegistrationGuide = ({ entityType, onEntityTypeChange }) => {
  const { t } = useLanguage();
  const selectedEntity = GST_REGISTRATION_ENTITY_GUIDE.find((item) => item.entityType === entityType) || GST_REGISTRATION_ENTITY_GUIDE[0];

  return (
    <Card className="p-5 rounded-2xl border border-[#14A098]/30 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="ui-title-md">{t('sf.gst_guide_title', 'GST Registration Documents')}</h3>
        <Badge className="bg-[#14A098]/10 text-[#0f2540] border border-[#14A098]/25">{t('sf.gst_guide_badge', 'Business type wise')}</Badge>
      </div>
      <p className="ui-note mb-4">{t('sf.gst_guide_note', 'Choose your business type to view required documents.')}</p>

      <div className="grid sm:grid-cols-3 gap-2 mb-4">
        {GST_REGISTRATION_ENTITY_GUIDE.map((item) => (
          <button
            key={item.entityType}
            type="button"
            onClick={() => onEntityTypeChange(item.entityType)}
            className={`rounded-xl border px-3 py-2 text-sm font-semibold text-left transition-colors ${
              entityType === item.entityType
                ? 'border-[#14A098] bg-teal-50 text-[#0f2540]'
                : 'border-gray-200 bg-white text-[#0f2540] hover:border-[#14A098]/40'
            }`}
          >
            <span className="block leading-snug">{t(`gstEntityGuide.${item.entityType}.label`, item.label)}</span>
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
        <h4 className="font-semibold text-[#0f2540] mb-3">
          {t('sf.gst_docs_title', 'Required documents ({label})', {
            label: t(`gstEntityGuide.${selectedEntity.entityType}.label`, selectedEntity.label),
          })}
        </h4>
        <ul className="space-y-2">
          {selectedEntity.docs.map((doc, idx) => (
            <li key={doc} className="flex items-start gap-2 text-sm text-gray-700">
              <CheckCircle2 className="w-4 h-4 text-[#14A098] mt-0.5 shrink-0" />
              <span>{t(`gstEntityGuide.${selectedEntity.entityType}.doc${idx}`, doc)}</span>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
};


const SuccessView = ({ payload, onAbout }) => {
  const { t } = useLanguage();
  useEffect(() => {
    const timer = setTimeout(() => onAbout(), 3000);
    return () => clearTimeout(timer);
  }, [onAbout]);

  const firstName = payload.full_name.split(' ')[0];
  const n = payload.service_required.length;

  return (
    <div className="pt-28 pb-16 hero-gradient min-h-screen">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <Card className="p-8 md:p-12 rounded-2xl border border-gray-100 shadow-xl bg-white text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-[#14A098] to-[#5eead4] flex items-center justify-center shadow-lg mb-6">
            <CheckCircle2 className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#0f2540] mb-3">{t('sf.success_thanks', 'Thank you, {name}!', { name: firstName })}</h1>
          <p className="text-gray-600 mb-6">{t('sf.success_body', 'We\'ve received your request for {n} service(s). Our expert will reach you at +91 {phone} within 2 hours.', { n, phone: payload.mobile })}</p>
          <p className="text-sm text-gray-500">{t('sf.success_redirect', 'Redirecting...')}</p>
        </Card>
      </div>
    </div>
  );
};

const ServicesForm = ({ embedded = false, useHomeLayout = false }) => {
  const { t, locale } = useLanguage();
  const homeLayout = embedded || useHomeLayout;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preCode = params.get('service');

  // isIncomeTaxService is an imported pure function — stable reference
  useEffect(() => {
    if (preCode && isIncomeTaxService(preCode)) {
      navigate(`/file-itr?service=${preCode}`, { replace: true });
    }
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preCode, navigate]);

  useEffect(
    () => trackPageLifecycle({
      funnel_name: 'SERVICES_MULTI_FORM',
      form_name: 'ServicesForm',
      service_code: preCode || null,
      open_event_name: 'services_form_open',
    }),
    [preCode],
  );

  const [form, setForm] = useState(() => ({
    ...initialState,
    language: defaultSpokenLanguageForUiLocale(locale),
    service_required: preCode && !isIncomeTaxService(preCode) ? [preCode] : [],
  }));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [gstEntityType, setGstEntityType] = useState('PROPRIETARY');
  const singleServiceMode = Boolean(preCode) && !embedded;
  // Standalone /get-started page: show the multi-service form immediately (no extra reveal click).
  // The embedded home-page version keeps the reveal button so the homepage doesn't jump straight to a long form.
  const [multiFormRevealed, setMultiFormRevealed] = useState(
    () => Boolean((preCode && !isIncomeTaxService(preCode)) || !embedded),
  );
  const showMultiForm = singleServiceMode || multiFormRevealed;
  const fieldRefs = useRef({});

  useEffect(() => {
    const def = defaultSpokenLanguageForUiLocale(locale);
    if (!def) return;
    setForm((prev) => (prev.language ? prev : { ...prev, language: def }));
  }, [locale]);

  const selectedServices = useMemo(
    () => SERVICE_CATALOG.filter((svc) => form.service_required.includes(svc.code)),
    [form.service_required],
  );

  const setField = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    if (submitError) setSubmitError('');
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const toggleService = (code) => {
    if (singleServiceMode) return;
    const wasSelected = form.service_required.includes(code);
    setForm((prev) => ({
      ...prev,
      service_required: prev.service_required.includes(code)
        ? prev.service_required.filter((c) => c !== code)
        : [...prev.service_required, code],
    }));
    trackEvent({
      event_name: wasSelected ? 'service_unselected' : 'service_selected',
      event_action: wasSelected ? 'remove' : 'select',
      event_status: 'success',
      event_category: 'form',
      form_name: 'ServicesForm',
      funnel_name: 'SERVICES_MULTI_FORM',
      funnel_step_number: 2,
      funnel_step_name: 'service_selection',
      journey_id: getJourneyId('SERVICES_MULTI_FORM'),
      service_code: code,
      event_label: code,
    });
    if (errors.service_required) setErrors((prev) => ({ ...prev, service_required: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validateForm(form, t);
    setErrors(errs);
    setSubmitError('');
    if (Object.keys(errs).length > 0) {
      trackEvent({
        event_name: 'services_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'ServicesForm',
        funnel_name: 'SERVICES_MULTI_FORM',
        funnel_step_number: 3,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('SERVICES_MULTI_FORM'),
        error_code: 'validation_error',
        error_message: 'Client-side validation failed',
      });
      toast.error(t('form.err_fix_toast', 'Please fix the highlighted errors'));
      const keyOrder = ['full_name', 'mobile', 'email', 'service_required'];
      const firstErrorKey = keyOrder.find((key) => errs[key]) || Object.keys(errs)[0];
      if (firstErrorKey === 'service_required') {
        const serviceInput = document.getElementById('services-search-input');
        if (serviceInput) serviceInput.focus();
      } else if (firstErrorKey && fieldRefs.current[firstErrorKey]) {
        fieldRefs.current[firstErrorKey].focus();
      }
      return;
    }
    trackEvent({
      event_name: 'services_submit_attempt',
      event_action: 'submit',
      event_status: 'attempt',
      event_category: 'form',
      form_name: 'ServicesForm',
      funnel_name: 'SERVICES_MULTI_FORM',
      funnel_step_number: 3,
      funnel_step_name: 'submit',
      journey_id: getJourneyId('SERVICES_MULTI_FORM'),
      phone_number: form.mobile,
      event_label: form.service_required.join(','),
    });
    setSubmitting(true);
    const capture = buildCampaignCapturePayload();
    const payload = {
      full_name: form.full_name.trim(),
      email: form.email.trim(),
      mobile: form.mobile.trim(),
      state: form.state || null,
      language: form.language || null,
      remark: form.remark.trim() || null,
      service_required: form.service_required,
      tag: 'client_website',
      ...capture,
      lead_source: capture.lead_source || 'WEBSITE',
    };
    try {
      if (!isGoogleSheetsConfigured()) {
        trackEvent({
          event_name: 'services_submit_failed',
          event_action: 'submit',
          event_status: 'failed',
          event_category: 'form',
          form_name: 'ServicesForm',
          funnel_name: 'SERVICES_MULTI_FORM',
          funnel_step_number: 3,
          funnel_step_name: 'submit',
          journey_id: getJourneyId('SERVICES_MULTI_FORM'),
          phone_number: form.mobile,
          event_label: form.service_required.join(','),
          error_code: 'missing_sheets_env',
          error_message: 'REACT_APP_GOOGLE_SHEETS_WEBAPP_URL not set',
        });
        toast.error(
          t(
            'form.err_sheets_missing',
            'Add REACT_APP_GOOGLE_SHEETS_WEBAPP_URL in frontend/.env (Apps Script URL ending in /exec), then restart the dev server.',
          ),
        );
        return;
      }
      appendLeadToGoogleSheets({
        form: embedded ? 'home_services_multi' : 'services_multi',
        summary: `${payload.full_name} | ${payload.mobile} | ${Array.isArray(payload.service_required) ? payload.service_required.join(', ') : '—'}`,
        detail: payload,
      });
      trackEvent({
        event_name: 'services_submit_success',
        event_action: 'submit',
        event_status: 'success',
        event_category: 'form',
        form_name: 'ServicesForm',
        funnel_name: 'SERVICES_MULTI_FORM',
        funnel_step_number: 3,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('SERVICES_MULTI_FORM', { reset: true }),
        phone_number: form.mobile,
        event_label: form.service_required.join(','),
        api_name: 'google_sheets',
        api_status_code: null,
      });
      toast.success(
        t('form.toast_submit_ok_public', 'Submitted successfully. Our team will contact you soon.'),
      );
      setSuccess(payload);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[SolveTax] submission failed', err);
      trackEvent({
        event_name: 'services_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'ServicesForm',
        funnel_name: 'SERVICES_MULTI_FORM',
        funnel_step_number: 3,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('SERVICES_MULTI_FORM'),
        phone_number: form.mobile,
        event_label: form.service_required.join(','),
        error_code: 'unexpected',
        error_message: String(err?.message || err),
      });
      toast.error(t('contact.toast_backend', 'Backend: {msg}', { msg: err?.message || 'Unexpected error' }));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) return <SuccessView payload={success} onAbout={() => goToPostSubmit(navigate, success.full_name)} />;

  const headlineTitle = preCode === 'GST_REGISTRATION'
    ? t('gstreg.headline', 'Register for GST Number & get in 2 hours')
    : preCode
      ? serviceTitle(preCode, t, SERVICE_CATALOG.find((s) => s.code === preCode)?.title || preCode)
      : t('sf.multi_head', 'You can request multiple services');
  const submitCtaLabel = homeLayout && !singleServiceMode
    ? t('sf.get_services', 'Get services')
    : t('form.submit_details', 'Submit Details');
  const isGstRegistrationSingle = Boolean(singleServiceMode && preCode === 'GST_REGISTRATION');

  const revealMultiServiceForm = () => {
    setMultiFormRevealed(true);
    trackEvent({
      event_name: 'cta_click',
      event_action: 'click',
      event_status: 'success',
      event_category: 'navigation',
      cta_name: 'reveal_multi_service_form',
      page_path: embedded ? '/home' : '/get-started',
      form_name: 'ServicesForm',
      funnel_name: 'SERVICES_MULTI_FORM',
    });
    window.setTimeout(() => {
      const el = document.getElementById('multi-service-form');
      if (el) scrollToAnchor(el, { behavior: 'smooth' });
    }, 80);
  };

  const personalDetailsSection = (
    <div className="space-y-8">
      <FormSection title={t('sf.primary', 'Primary Details')} icon={User}>
        <div className="grid md:grid-cols-2 gap-5">
          <FieldWrap htmlFor="sf-full_name" label={t('sf.full_name', 'Full Name')} icon={User} required error={errors.full_name}>
            <Input id="sf-full_name" name="full_name" autoComplete="name" ref={(el) => { fieldRefs.current.full_name = el; }} value={form.full_name} onChange={(e) => setField('full_name', e.target.value)} placeholder={t('sf.placeholder_name', 'e.g. Anita Verma')} maxLength={150} />
          </FieldWrap>
          <FieldWrap htmlFor="sf-mobile" label={t('sf.mobile', 'Mobile Number')} icon={Phone} required error={errors.mobile}>
            <Input id="sf-mobile" name="mobile" autoComplete="tel" ref={(el) => { fieldRefs.current.mobile = el; }} value={form.mobile} onChange={(e) => setField('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder={t('itr.placeholder_mobile', '10-digit mobile')} inputMode="numeric" />
          </FieldWrap>
          <FieldWrap htmlFor="sf-email" label={t('sf.email', 'Email Address')} icon={Mail} required error={errors.email}>
            <Input id="sf-email" name="email" autoComplete="email" ref={(el) => { fieldRefs.current.email = el; }} type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder={t('sf.placeholder_email', 'your@email.com')} />
          </FieldWrap>
        </div>
        <div className="pt-2">
          <Button type="button" variant="outline" onClick={() => setShowMoreDetails((prev) => !prev)} className="rounded-full border-[#14A098] text-[#14A098] hover:bg-[#14A098] hover:text-white">
            {showMoreDetails ? t('sf.hide_more', 'Hide additional details') : t('sf.add_more', 'Add more details (optional)')}
          </Button>
        </div>
        {showMoreDetails && (
          <div className="grid md:grid-cols-2 gap-5 pt-4">
            <FieldWrap htmlFor="sf-language" label={t('sf.pref_lang', 'Preferred Language')} icon={Globe}>
              <Select value={form.language} onValueChange={(v) => setField('language', v)}>
                <SelectTrigger id="sf-language" name="language" aria-label={t('sf.pref_lang', 'Preferred Language')}><SelectValue placeholder={t('itr.placeholder_lang', 'Select language')} /></SelectTrigger>
                <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </FieldWrap>
            <FieldWrap htmlFor="sf-state" label={t('sf.state', 'State')} icon={MapPin}>
              <Select value={form.state} onValueChange={(v) => setField('state', v)}>
                <SelectTrigger id="sf-state" name="state" aria-label={t('sf.state', 'State')}><SelectValue placeholder={t('itr.placeholder_state', 'Select state')} /></SelectTrigger>
                <SelectContent className="max-h-72">{INDIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </FieldWrap>
          </div>
        )}
      </FormSection>
      {showMoreDetails && (
        <FormSection title={t('sf.remark_section', 'Additional Remarks')} icon={MessageSquare}>
          <Textarea id="sf-remark" name="remark" value={form.remark} onChange={(e) => setField('remark', e.target.value)} rows={3} placeholder={t('sf.remark_ph', 'Any specific requirement or timeline (optional)')} />
        </FormSection>
      )}
    </div>
  );

  const formFooterActions = (
    <div className="flex flex-wrap gap-3 pt-2 items-center">
      <Button type="submit" size="lg" disabled={submitting} className="bg-[#14A098] hover:bg-[#0e7a74] text-white rounded-full px-8 h-12 min-w-[200px] shadow-lg shadow-[#14A098]/30 whitespace-normal text-center leading-snug">
        {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('itr.submitting', 'Submitting...')}</>) : (<>{submitCtaLabel} <ArrowRight className="w-4 h-4 ml-2" /></>)}
      </Button>
      <Button
        type="button"
        variant="outline"
        onClick={() => {
          if (isGstRegistrationSingle) setGstEntityType('PROPRIETARY');
          setForm({
            ...initialState,
            language: defaultSpokenLanguageForUiLocale(locale),
            service_required: preCode && !isIncomeTaxService(preCode) ? [preCode] : [],
          });
        }}
        className="rounded-full h-12 border-gray-300"
      >
        {t('itr.reset', 'Reset')}
      </Button>
      <FormLegalNotice />
    </div>
  );

  return (
    <div className={embedded ? 'py-8 bg-white' : 'pt-28 pb-16 hero-gradient min-h-screen'}>
      <div className={`${homeLayout ? 'w-full max-w-[1680px]' : 'max-w-[1500px]'} mx-auto px-3 md:px-6`}>
        {embedded && !singleServiceMode && (
          <div className="mb-6 md:mb-8 w-full min-w-0">
            <ServiceMarquee linkable />
          </div>
        )}

        <div className={`text-center ${embedded ? 'mb-6' : 'mb-10'}`}>
          {!singleServiceMode ? (
            <div className="max-w-2xl mx-auto mb-6 rounded-3xl rotating-border-card px-5 py-7 md:py-9">
              <div className="flex flex-wrap items-center justify-center gap-2 md:gap-2.5 mb-5">
                <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-[#14A098] to-[#0e7a74] shadow-md shadow-[#14A098]/30">
                  <LayoutGrid className="w-3.5 h-3.5 text-white shrink-0" />
                  <span className="text-[11px] md:text-xs font-bold text-white whitespace-nowrap">{t('sf.multi_badge_1', 'Multi-service planner')}</span>
                </div>
                <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 shadow-md shadow-cyan-500/30">
                  <Sparkles className="w-3.5 h-3.5 text-white shrink-0" />
                  <span className="text-[11px] md:text-xs font-bold text-white whitespace-nowrap">{t('sf.multi_badge_2', 'All in one place')}</span>
                </div>
                <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 shadow-md shadow-emerald-500/30">
                  <MousePointerClick className="w-3.5 h-3.5 text-white shrink-0" />
                  <span className="text-[11px] md:text-xs font-bold text-white whitespace-nowrap">{t('sf.multi_badge_3', 'Pick one or more services')}</span>
                </div>
              </div>
              <h1 className={`${homeLayout ? 'ui-title-lg' : 'ui-title-xl'} mb-0`}>{headlineTitle}</h1>
            </div>
          ) : (
            <h1 className={`${homeLayout ? 'ui-title-lg' : 'ui-title-xl'} mb-5`}>{headlineTitle}</h1>
          )}
          {!singleServiceMode && (
            <div className="max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-lg shadow-[#14A098]/25 mb-7 flex flex-col sm:flex-row bg-gradient-to-r from-[#14A098] via-cyan-500 to-emerald-500">
              <div className="flex-1 p-4 text-white text-left border-b sm:border-b-0 sm:border-r border-white/25">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-extrabold shrink-0">1</span>
                  <FileCheck2 className="w-4 h-4 shrink-0" />
                </div>
                <div className="font-semibold text-sm leading-snug">{t('sf.flow_step1', 'Select the services & submit')}</div>
              </div>
              <div className="flex-1 p-4 text-white text-left border-b sm:border-b-0 sm:border-r border-white/25">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-extrabold shrink-0">2</span>
                  <PhoneCall className="w-4 h-4 shrink-0" />
                </div>
                <div className="font-semibold text-sm leading-snug">{t('sf.flow_step2', 'Our CA will reach out to you')}</div>
              </div>
              <div className="flex-1 p-4 text-white text-left">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-extrabold shrink-0">3</span>
                  <ShieldCheck className="w-4 h-4 shrink-0" />
                </div>
                <div className="font-semibold text-sm leading-snug">{t('sf.flow_step3', 'Get all services in one place with SolveTax')}</div>
              </div>
            </div>
          )}
          {!singleServiceMode && !multiFormRevealed && (
            <div className="flex justify-center mb-6 md:mb-8">
              <Button
                type="button"
                size="lg"
                onClick={revealMultiServiceForm}
                className="rounded-full h-12 px-8 bg-[#14A098] hover:bg-[#0e7a74] text-white border-2 border-[#14A098] shadow-lg shadow-[#14A098]/30 font-semibold"
              >
                {t('sf.request_multi_btn', 'Request one or multiple services')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </div>

        {showMultiForm && (
        <div id="multi-service-form" className={`grid ${homeLayout ? 'lg:grid-cols-1' : 'lg:grid-cols-12'} gap-6 scroll-mt-24`}>
          {!homeLayout && (
            <div className="lg:col-span-4 lg:order-1">
              <Sidebar count={form.service_required.length} errors={errors} submitError={submitError} />
            </div>
          )}
          {isGstRegistrationSingle ? (
            <div className={`${homeLayout ? '' : 'lg:col-span-8 lg:order-2'} space-y-6`}>
              <Card className="p-6 md:p-10 rounded-2xl border border-gray-100 shadow-xl bg-white">
                <form onSubmit={handleSubmit} className="space-y-8">
                  {submitError && (
                    <div className="rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                      <p className="font-medium">{submitError}</p>
                      <p className="mt-1">
                        {t('form.need_help_case', 'Need help with this case?')}{' '}
                        <Link to="/contact" className="font-semibold underline hover:text-rose-800">
                          {t('form.contact_support', 'Contact support')}
                        </Link>
                      </p>
                    </div>
                  )}
                  {personalDetailsSection}
                  {formFooterActions}
                </form>
              </Card>
              <GSTRegistrationGuide entityType={gstEntityType} onEntityTypeChange={setGstEntityType} />
            </div>
          ) : (
            <Card className={`${homeLayout ? '' : 'lg:col-span-8 lg:order-2'} p-6 md:p-10 rounded-2xl border border-gray-100 shadow-xl bg-white`}>
              <form onSubmit={handleSubmit} className="space-y-8">
                {submitError && (
                  <div className="rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    <p className="font-medium">{submitError}</p>
                    <p className="mt-1">
                      {t('form.need_help_case', 'Need help with this case?')}{' '}
                      <Link to="/contact" className="font-semibold underline hover:text-rose-800">
                        {t('form.contact_support', 'Contact support')}
                      </Link>
                    </p>
                  </div>
                )}
                <div className={`grid ${singleServiceMode ? 'grid-cols-1' : 'xl:grid-cols-2'} gap-8 items-start`}>
                  <div>{personalDetailsSection}</div>

                  {!singleServiceMode && (
                    <div className="xl:sticky xl:top-24">
                      <FormSection title={t('sf.services_req', 'Services Required')} icon={Briefcase} hint={t('sf.select_multi', 'Select multiple if needed')}>
                        <ServicePicker selected={form.service_required} onToggle={toggleService} error={errors.service_required} includeIncomeTax={homeLayout} />
                      </FormSection>
                    </div>
                  )}
                </div>

                {!singleServiceMode && selectedServices.length > 0 && (
                  <div className="rounded-2xl border-2 border-[#14A098]/35 bg-gradient-to-br from-teal-50/90 to-white p-4 md:p-5 shadow-sm">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#14A098] mb-2">
                      {t('sf.review_before_submit', 'Review before submit')}
                    </p>
                    <p className="text-sm font-semibold text-[#0f2540] mb-3">
                      {selectedServices.length > 1
                        ? t('sf.service_count_plural', '{n} services selected', { n: selectedServices.length })
                        : t('sf.service_count', '{n} service selected', { n: selectedServices.length })}
                    </p>
                    <div className="mb-3 grid gap-2 sm:grid-cols-2">
                      {selectedServices.map((svc) => {
                        return (
                          <div key={svc.code} className="rounded-lg border border-[#14A098]/25 bg-white px-3 py-2">
                            <p className="text-xs font-semibold text-[#0f2540] leading-snug">
                              {serviceTitle(svc.code, t, svc.title)}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {formFooterActions}
              </form>
            </Card>
          )}
        </div>
        )}
        {singleServiceMode && SERVICE_CONTENT[preCode] && (
          <ServiceContent
            code={preCode}
            title={serviceTitle(preCode, t, SERVICE_CATALOG.find((s) => s.code === preCode)?.title || preCode)}
            content={SERVICE_CONTENT[preCode]}
          />
        )}
      </div>
    </div>
  );
};

export default ServicesForm;
